import {BACKEND_SERVICES} from './backendServices';
import type {BackendServiceName} from './backendServices';
import type {ProjectConfig} from './types';

/**
 * Joins URL segments, normalizing away extra slashes at boundaries.
 * Undefined/empty parts are ignored.
 *
 * @example
 * joinPath('https://example.com/', '/counts') // - 'https://example.com/counts'
 * joinPath('https://example.com',  'counts')  // - 'https://example.com/counts'
 * joinPath('https://example.com',  undefined) // - 'https://example.com'
 */
const joinPath = (base: string, ...parts: (string | undefined)[]): string => {
  const normalized = base.replace(/\/+$/, '');
  const rest = parts
    .filter((p): p is string => !!p)
    .map((p) => p.replace(/^\/+/, '').replace(/\/+$/, ''));
  return [normalized, ...rest].join('/');
};

/**
 * Named project configurations for each target used by the app.
 *
 * Each config owns all URL construction logic for its environment, including
 * staging/prod switching and sub-path composition. Callers specify only a
 * service/function name and an optional sub-path via RequestOptions.
 */
export const PROJECT_CONFIGS = {
  /**
   * The consolidated backend (ADR-0019/0020): one Cloud Run service per entrypoint and stage,
   * each with its base URL in one env var per environment — so the BMAPS-3721 load-balancer
   * cutover is an env-value change, service by service, with no code change.
   *
   * @example
   * createHttpClient('BACKEND').get('internal-public-api', {path: 'api/hello'})
   * // - https://internal-public-api-staging-342996354261.us-central1.run.app/api/hello
   *
   * @example Speculative, once the BMAPS-3721 load balancer routes by path and strips the env
   * prefix — same call, VUE_APP_INTERNAL_PUBLIC_API_URL='/beta':
   * // - /beta/api/hello
   */
  BACKEND: {
    // isProd is unused: each environment's env values already target that environment's stage.
    getUrl: (serviceName, isProd, path) => {
      const definition = BACKEND_SERVICES[serviceName as BackendServiceName];
      if (!definition) {
        throw new Error(`Unknown backend service "${serviceName}". Add it to BACKEND_SERVICES.`);
      }
      const base = process.env[definition.envVar];
      if (!base) {
        throw new Error(`${definition.envVar} is required for backend URLs (service "${serviceName}").`);
      }
      return joinPath(base, path);
    },
  } satisfies ProjectConfig,

  /**
   * The primary internal BMAPS Cloud Functions project.
   * Base URL is sourced from VUE_APP_CLOUD_FUNCTIONS_URL; function names get a '-staging' suffix outside prod.
   */
  INTERNAL: {
    getUrl: (serviceName, isProd, path) =>
      joinPath(
        process.env.VUE_APP_CLOUD_FUNCTIONS_URL ?? '',
        `${serviceName}${isProd ? '' : '-staging'}`,
        path,
      ),
  } satisfies ProjectConfig,

  /**
   * The Shareable Maps (SMAPS) project.
   * Switches base URL between shareablemaps-prod and shareablemaps-dev per environment.
   */
  SHARED_MAPS: {
    getUrl: (serviceName, isProd, path) => {
      const base = isProd
        ? 'https://us-central1-shareablemaps-prod.cloudfunctions.net'
        : 'https://us-central1-shareablemaps-dev.cloudfunctions.net';
      return joinPath(base, `${serviceName}${isProd ? '' : '-staging'}`, path);
    },
  } satisfies ProjectConfig,

  /**
   * The northpoint-production GCP project.
   * Base URL is always northpoint-production; staging/prod is indicated by the function name suffix.
   */
  NORTHPOINT_PRODUCTION: {
    getUrl: (serviceName, isProd, path) =>
      joinPath(
        'https://us-central1-northpoint-production.cloudfunctions.net',
        `${serviceName}${isProd ? '' : '-staging'}`,
        path,
      ),
  } satisfies ProjectConfig,

  /**
   * BigQuery endpoint.
   * Switches base URL per environment; no staging suffix on function names.
   */
  BIG_QUERY: {
    getUrl: (serviceName, isProd, path) => {
      const base = isProd
        ? 'https://us-central1-northpoint-production.cloudfunctions.net'
        : 'https://us-central1-beyondmaps-dev.cloudfunctions.net';
      return joinPath(base, serviceName, path);
    },
  } satisfies ProjectConfig,

  /**
   * Internal project resolved via VUE_APP_GCP_PROJECT_ID.
   * Targets whichever BMAPS project the app is running in (beyondmaps-dev in staging, northpoint-production in prod).
   */
  INTERNAL_PROJECT: {
    getUrl: (serviceName, isProd, path) =>
      joinPath(
        `https://us-central1-${process.env.VUE_APP_GCP_PROJECT_ID ?? ''}.cloudfunctions.net`,
        `${serviceName}${isProd ? '' : '-staging'}`,
        path,
      ),
  } satisfies ProjectConfig,

  /**
   * Cloud Run services.
   * Constructs the URL as: https://{serviceName}{suffix}-{projectNumber}.us-central1.run.app/{path}
   * where {suffix} is '-staging' outside prod and empty in prod, and {projectNumber} is
   * VUE_APP_GCP_PROJECT_NUMBER.
   *
   * Cloud Run services should follow the naming convention: {base}-staging / {base}.
   *
   * @example
   * createHttpClient('CLOUD_RUN').post('filtermarkers', body, {path: 'counts'})
   * // - https://filtermarkers-staging-342996354261.us-central1.run.app/counts
   * // - https://filtermarkers-986447360139.us-central1.run.app/counts (prod)
   */
  CLOUD_RUN: {
    getUrl: (serviceName, isProd, path) => {
      if (process.env.VUE_APP_USE_LOCAL_BACKENDS === 'true') {
        return joinPath(`/${serviceName}`, path);
      }
      const suffix = isProd ? '' : '-staging';
      const projectNumber = process.env.VUE_APP_GCP_PROJECT_NUMBER ?? '';
      if (!projectNumber && process.env.VUE_APP_USE_LOCAL_BACKENDS !== 'true') {
        throw new Error('VUE_APP_GCP_PROJECT_NUMBER is required for CLOUD_RUN URLs');
      }
      return joinPath(`https://${serviceName}${suffix}-${projectNumber}.us-central1.run.app`, path);
    },
  } satisfies ProjectConfig,
} as const;

export type ProjectKey = keyof typeof PROJECT_CONFIGS;
